import { useState, useEffect, useRef, useCallback } from 'react';
import type { OfflineBannerProps, OfflineBannerStatus } from './types';

// ─── Status configuration ───────────────────────────────────────

interface StatusConfig {
  bg: string;
  icon: string;
  iconAnimate?: string;
  role: 'alert' | 'status';
  ariaLive: 'assertive' | 'polite';
  dismissable: boolean;
}

const STATUS_CONFIG: Record<Exclude<OfflineBannerStatus, 'online'>, StatusConfig> = {
  offline: {
    bg: 'bg-warning-500',
    icon: '⊘', // TODO: replace with icon library (cloud-off)
    role: 'alert',
    ariaLive: 'assertive',
    dismissable: true,
  },
  syncing: {
    bg: 'bg-brand-500',
    icon: '↻', // TODO: replace with icon library (loader)
    iconAnimate: 'animate-spin',
    role: 'status',
    ariaLive: 'polite',
    dismissable: false,
  },
  conflict: {
    bg: 'bg-danger-500',
    icon: '⚠', // TODO: replace with icon library (alert-triangle)
    role: 'alert',
    ariaLive: 'assertive',
    dismissable: false,
  },
  restored: {
    bg: 'bg-success-500',
    icon: '✓', // TODO: replace with icon library (check-circle)
    role: 'status',
    ariaLive: 'polite',
    dismissable: true,
  },
};

/** Auto-dismiss delay for the "restored" state */
const RESTORED_DISMISS_MS = 3000;

/** Transition duration for exit animation */
const EXIT_ANIMATION_MS = 150;

// ─── Message helpers ────────────────────────────────────────────

function getOfflineMessage(
  isMobile: boolean,
  queueCount: number,
): string {
  if (isMobile) {
    return queueCount > 0
      ? `Offline — ${queueCount} bill${queueCount !== 1 ? 's' : ''} saved locally`
      : 'Offline — bills saved locally';
  }
  return queueCount > 0
    ? `Offline — billing continues normally. ${queueCount} bill${queueCount !== 1 ? 's' : ''} saved locally.`
    : 'Offline — billing continues normally. Bills saved locally.';
}

function getSyncingMessage(
  isMobile: boolean,
  queueCount: number,
  syncProgress?: { current: number; total: number },
): string {
  if (syncProgress) {
    return isMobile
      ? `Syncing ${syncProgress.current}/${syncProgress.total}...`
      : `Syncing ${syncProgress.current} of ${syncProgress.total} transactions...`;
  }
  return isMobile
    ? `Syncing ${queueCount} pending...`
    : `Syncing ${queueCount} pending transaction${queueCount !== 1 ? 's' : ''}...`;
}

function getConflictMessage(conflictCount: number): string {
  return `${conflictCount} sync conflict${conflictCount !== 1 ? 's' : ''} need resolution`;
}

function getRestoredMessage(isMobile: boolean): string {
  return isMobile
    ? 'Back online — syncing'
    : 'Back online — your data is syncing';
}

// ─── OfflineBanner Component ────────────────────────────────────

export function OfflineBanner({
  status,
  queueCount = 0,
  conflictCount = 0,
  syncProgress,
  onConflictClick,
  onDismiss,
  breakpoint = 'desktop',
}: OfflineBannerProps) {
  // Track whether the banner is visually shown (for enter/exit animation)
  const [shown, setShown] = useState(false);
  // Track whether the DOM element should be rendered
  const [mounted, setMounted] = useState(false);

  const dismissTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const exitTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const isActive = status !== 'online';
  const isMobile = breakpoint === 'mobile';

  // ─── Mount/unmount with animation ─────────────────────────────

  useEffect(() => {
    if (isActive) {
      // Mount → next frame show (for CSS transition to play)
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShown(true);
        });
      });
    } else {
      // Hide → after transition unmount
      setShown(false);
      exitTimerRef.current = setTimeout(() => {
        setMounted(false);
      }, EXIT_ANIMATION_MS);
    }

    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, [isActive]);

  // ─── Auto-dismiss for "restored" state ────────────────────────

  const handleDismiss = useCallback(() => {
    setShown(false);
    exitTimerRef.current = setTimeout(() => {
      setMounted(false);
      onDismiss?.();
    }, EXIT_ANIMATION_MS);
  }, [onDismiss]);

  useEffect(() => {
    if (status === 'restored') {
      dismissTimerRef.current = setTimeout(handleDismiss, RESTORED_DISMISS_MS);
    }
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [status, handleDismiss]);

  // ─── Don't render when fully hidden ───────────────────────────

  if (!mounted) return null;

  const config = STATUS_CONFIG[status as Exclude<OfflineBannerStatus, 'online'>];
  if (!config) return null;

  // ─── Build message ────────────────────────────────────────────

  let message: string;
  switch (status) {
    case 'offline':
      message = getOfflineMessage(isMobile, queueCount);
      break;
    case 'syncing':
      message = getSyncingMessage(isMobile, queueCount, syncProgress);
      break;
    case 'conflict':
      message = getConflictMessage(conflictCount);
      break;
    case 'restored':
      message = getRestoredMessage(isMobile);
      break;
    default:
      return null;
  }

  // ─── Progress bar percentage ──────────────────────────────────

  const progressPercent = syncProgress && syncProgress.total > 0
    ? Math.round((syncProgress.current / syncProgress.total) * 100)
    : null;

  return (
    <div
      role={config.role}
      aria-live={config.ariaLive}
      className={[
        'relative flex w-full items-center justify-center gap-2',
        'font-medium text-white',
        'z-fixed',
        // Responsive height + text
        isMobile ? 'h-8 text-[11px]' : 'h-offline-banner text-xs',
        // Background color
        config.bg,
        // Enter/exit transition
        'transition-all',
        shown
          ? 'translate-y-0 opacity-100 duration-200 ease-out'
          : '-translate-y-full opacity-0 duration-150 ease-in',
      ].join(' ')}
    >
      {/* Icon */}
      <span
        className={[
          'flex-shrink-0',
          config.iconAnimate ?? '',
        ].join(' ')}
        aria-hidden="true"
      >
        {config.icon}
      </span>

      {/* Message */}
      <span className="truncate">{message}</span>

      {/* Conflict "Resolve" action button */}
      {status === 'conflict' && onConflictClick && (
        <button
          type="button"
          onClick={onConflictClick}
          className={[
            'ml-2 flex-shrink-0 rounded border border-white/20 px-2 py-0.5',
            'text-[11px] font-semibold text-white',
            'hover:bg-white/10 active:bg-white/20',
            'transition-colors duration-150',
          ].join(' ')}
          aria-label="Resolve sync conflicts"
        >
          Resolve
        </button>
      )}

      {/* Dismiss button (offline + restored only) */}
      {config.dismissable && onDismiss && (
        <button
          type="button"
          onClick={handleDismiss}
          className={[
            'absolute right-2 top-1/2 -translate-y-1/2',
            'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded',
            'text-white/80 hover:bg-white/20 hover:text-white',
            'transition-colors duration-150',
          ].join(' ')}
          aria-label="Dismiss notification"
        >
          {/* TODO: replace with icon library */}
          <span className="text-xs" aria-hidden="true">✕</span>
        </button>
      )}

      {/* Sync progress bar */}
      {status === 'syncing' && progressPercent !== null && (
        <div
          className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/20"
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Sync progress"
        >
          <div
            className="h-full bg-white transition-[width] duration-300 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}
    </div>
  );
}
